import { useEffect, useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Datasource, searchService } from 'domain/documents/api/searchService';

export const MIN_SEARCH_LENGTH = 3;
const documentSearchQueryKey = 'documentSearch';

type UseDocumentSearchOptions = {
  pageSize?: number;
  datasource?: Datasource;
  enabled?: boolean;
};

export default function useDocumentSearch(
  query: string,
  { pageSize = 10, datasource, enabled = true }: UseDocumentSearchOptions = {},
) {
  const trimmedQuery = query.trim();
  const isSearchable = enabled && trimmedQuery.length >= MIN_SEARCH_LENGTH;

  const [currentPage, setCurrentPage] = useState(1);

  // Une nouvelle recherche repart du début : conserver la page courante afficherait le milieu d'un
  // jeu de résultats que l'utilisateur n'a jamais parcouru.
  useEffect(() => setCurrentPage(1), [trimmedQuery, datasource, pageSize]);

  const { data, isFetching, isError, refetch } = useQuery({
    queryKey: [documentSearchQueryKey, trimmedQuery, datasource, pageSize, currentPage],
    queryFn: () => searchService.search({ query: trimmedQuery, currentPage, pageSize, datasource }),
    enabled: isSearchable,
    // Garder la page précédente à l'écran pendant le chargement de la suivante évite que la liste
    // ne disparaisse à chaque clic de pagination.
    placeholderData: keepPreviousData,
  });

  const results = data?.results ?? [];

  // `totalResult` compte les correspondances **avant déduplication** : le contrat interdit d'en
  // déduire un nombre de pages. Une page pleine est le seul indice fiable qu'une suite existe.
  const hasNextPage = results.length === pageSize;
  const hasPreviousPage = currentPage > 1;

  return {
    results,
    totalResult: data?.totalResult ?? 0,
    isLoading: isSearchable && isFetching,
    isError,
    retry: refetch,
    currentPage,
    hasNextPage,
    hasPreviousPage,
    goToNextPage: () => hasNextPage && setCurrentPage(page => page + 1),
    goToPreviousPage: () => hasPreviousPage && setCurrentPage(page => page - 1),
  };
}
